import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { buildableFromShelf, can, currentRevisionOf, designTree, structureView, summarizeDesign, withDraftStructure } from '@kronos/domain'
import type { DesignLineRef, DesignSummary, StructureView } from '@kronos/domain'
import { repository } from '../../data'
import type { ComponentType, Revision, StockRow } from '../../data'

/**
 * Everything "How it's made" draws, derived from three reads every other
 * screen already makes — the item master, the revisions and the shelf.
 *
 * **No endpoint of its own.** The explosion, the folding and the netting are
 * pure functions in `packages/domain/` over data the client already caches,
 * so changing the root or the quantity is instant and there is no second
 * server-side copy of the BOM walk to keep in step with `designTree`.
 */

/**
 * What is free on the shelf for one item: loose serials for a serialized item,
 * the ledger's sum for a counted one — the two halves `/api/parts` reports.
 *
 * One known difference from a build board's figure: a unit whose build has
 * started sits in `spares` as an empty shell, and the board does not count it
 * (`onHandByType` in `server/src/db/builds.ts`). This screen does. It only
 * matters while a build order for that very assembly is under way.
 */
export function onShelf(row: StockRow): number {
  return row.tracking === 'serialized' ? row.spares : row.onHand
}

export interface StructureData {
  readonly status: 'loading' | 'error' | 'unknown_item' | 'not_assembled' | 'ready'
  readonly type: ComponentType | undefined
  readonly revision: Revision | undefined
  readonly view: StructureView | null
  readonly summary: DesignSummary | null
  /** Roots the shelf could build right now, capped at 99. Null while stock loads. */
  readonly buildable: number | null
  readonly byId: ReadonlyMap<string, ComponentType>
  readonly canEdit: boolean
  /** False if the shelf could not be read: draw the structure, hide the counts. */
  readonly stockKnown: boolean
}

/**
 * An unsaved parts list to draw instead of the stored one — the item editor's
 * preview. The root's lines (and its name and shape) are swapped in by
 * `withDraftStructure`; everything beneath is the real item master.
 */
export interface StructureDraft {
  readonly type: ComponentType
  readonly lines: readonly DesignLineRef[]
}

export function useStructure(rootTypeId: string, quantity: number, draft?: StructureDraft): StructureData {
  const types = useQuery({ queryKey: ['component-types'], queryFn: () => repository.componentTypes() })
  const revisions = useQuery({ queryKey: ['revisions'], queryFn: () => repository.revisions() })
  const stock = useQuery({ queryKey: ['stock'], queryFn: () => repository.stock() })
  const me = useQuery({ queryKey: ['me'], queryFn: () => repository.me() })

  const model = useMemo(() => {
    const all = types.data ?? []
    const byId = new Map(all.map((t) => [t.id, t]))
    if (draft) byId.set(draft.type.id, draft.type)
    const shelf = new Map((stock.data ?? []).map((r) => [r.id, onShelf(r)]))
    const onHand = (id: string): number => shelf.get(id) ?? 0
    const stored = { types: all, revisions: revisions.data ?? [] }
    const source = draft ? withDraftStructure(stored, draft).source : stored
    const revision = currentRevisionOf(source.revisions, rootTypeId) as Revision | undefined
    const design = revision === undefined ? null : designTree(revision.id, source)
    return {
      byId,
      revision,
      design,
      view: design === null ? null : structureView(design, onHand, quantity),
      summary: design === null ? null : summarizeDesign(design),
      buildable: design === null || stock.data === undefined ? null : buildableFromShelf(design, onHand),
    }
  }, [types.data, revisions.data, stock.data, rootTypeId, quantity, draft])

  const type = model.byId.get(rootTypeId)
  const status: StructureData['status'] =
    types.isPending || revisions.isPending || stock.isPending
      ? 'loading'
      : types.isError || revisions.isError
        ? 'error'
        : type === undefined
          ? 'unknown_item'
          : model.view === null || model.revision?.lines.length === 0
            ? 'not_assembled'
            : 'ready'

  return {
    status,
    type,
    revision: model.revision,
    view: model.view,
    summary: model.summary,
    buildable: model.buildable,
    byId: model.byId,
    // Nothing to edit from inside a preview of the form that is doing the editing.
    canEdit: draft === undefined && can(me.data?.roles ?? [], 'edit_catalog'),
    stockKnown: stock.data !== undefined,
  }
}
